/**
 * Olfacy slideshow with segmented autoplay progress bars.
 * - Swiping is native scroll-snap.
 * - The progress fill is a CSS animation (compositor only); when it ends we move to the next slide.
 * - Pausing = animation-play-state: paused (via the [paused] attribute), so progress resumes where it stopped.
 */
class OlfacySlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.bars = [...this.querySelectorAll('[data-bar]')];
    if (!this.track || this.slides.length < 2) return;

    this.index = 0;
    this.autoplay = this.hasAttribute('data-autoplay');
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.bars.forEach((bar, i) => bar.addEventListener('click', () => this.go(i), { signal }));
    this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });

    if (this.autoplay) {
      this.querySelector('[data-bars]')?.addEventListener(
        'animationend',
        (event) => {
          if (event.target.closest('.oslide__bar') === this.bars[this.index]) this.go(this.index + 1);
        },
        { signal }
      );

      this.pauseReasons = new Set();
      const hold = (reason) => () => this.setPaused(reason, true);
      const release = (reason) => () => this.setPaused(reason, false);
      this.addEventListener('pointerenter', hold('hover'), { signal });
      this.addEventListener('pointerleave', release('hover'), { signal });
      this.addEventListener('focusin', hold('focus'), { signal });
      this.addEventListener('focusout', release('focus'), { signal });
      document.addEventListener('visibilitychange', () => this.setPaused('hidden', document.hidden), { signal });

      this.observer = new IntersectionObserver(([entry]) => this.setPaused('offscreen', !entry.isIntersecting));
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
  }

  setPaused(reason, on) {
    if (on) this.pauseReasons.add(reason);
    else this.pauseReasons.delete(reason);
    this.toggleAttribute('paused', this.pauseReasons.size > 0);
  }

  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      const width = this.track.clientWidth || 1;
      const i = Math.round(this.track.scrollLeft / width);
      if (i !== this.index) this.setActive(i); // user swiped
    });
  };

  go(i) {
    const target = (i + this.slides.length) % this.slides.length;
    this.setActive(target);
    this.track.scrollTo({ left: this.slides[target].offsetLeft });
  }

  setActive(i) {
    this.index = i;
    this.bars.forEach((bar, j) => {
      bar.classList.toggle('is-done', j < i);
      bar.classList.remove('is-active');
      if (j === i) bar.setAttribute('aria-current', 'true');
      else bar.removeAttribute('aria-current');
    });
    const active = this.bars[i];
    if (active) {
      void active.offsetWidth; // restart the fill animation on this one small element
      active.classList.add('is-active');
    }
  }
}

if (!customElements.get('olfacy-slideshow')) {
  customElements.define('olfacy-slideshow', OlfacySlideshow);
}
